import { isValidElement, type ReactElement } from 'react';
import {
  EMessageDirection,
  EMessageType,
  EWidgetType,
  type IChatMessage,
} from '@kognitos/lattice';

import { WORK_BLOCK_ID_PREFIX } from '../constants';
import type { TRainWorkBlockRenderer } from '../hooks/types';
// Leaf modules, not the `../components` barrel: that barrel pulls in every
// card, and this runs on the render path of a dozen chat surfaces.
import { RainReasoningRow } from '../components/RainReasoningRow';
import { RainToolActivity } from '../components/RainToolActivity';
import { RainWorkBlock } from '../components/RainWorkBlock';

/**
 * A copy-only tool-call row (`RainToolActivity`). Named structurally, the way
 * `RainChatView` already names `RainToolCallCard` — both live in the core beside
 * the caller, so naming them costs nothing.
 */
const isActivityRow = (message: IChatMessage): boolean =>
  isValidElement(message.widget?.customContent) &&
  message.widget.customContent.type === RainToolActivity;

/** A streamed reasoning stretch: prose in `content`, no widget. */
const isReasoningRow = (message: IChatMessage): boolean =>
  message.type === EMessageType.THINKING;

/**
 * Rows that make up a stretch of work, and so fold together.
 *
 * Reasoning counts as work, for two reasons. It arrives only from a
 * reasoning-capable model (`ReasoningText` in rain's `event.proto`), so whether
 * it appears is a model setting rather than a UI one — treating it as a seam
 * would let that setting fragment the transcript. And lattice folds runs of
 * reasoning into a `ChatThinkingMessage` of its own, so excluding it here
 * leaves two collapsed boxes of identical weight competing in one transcript.
 */
const isWorkRow = (message: IChatMessage): boolean =>
  isActivityRow(message) || isReasoningRow(message);

/**
 * Entries that render NOTHING, and so cannot be a seam.
 *
 * `generation_complete` appends one per tool-loop cycle, and an agent completes
 * several cycles per stretch of work. Ending a run on one splits it into boxes
 * with nothing visible between them to account for the split.
 */
const isTransparent = (message: IChatMessage): boolean =>
  message.type === EMessageType.COMPLETE;

/** The id a run's header is addressed by — stable as the run grows. */
const runId = (rows: IChatMessage[]): string =>
  `${WORK_BLOCK_ID_PREFIX}${rows[0].id}`;

/** Constant, so a growing transcript does not remount the header under it. */
const LIVE_RUN_ID = `${WORK_BLOCK_ID_PREFIX}live`;

const ignoreExpandedChange = (): void => {};

/** Wraps the block that stands in for one run as a message. */
const asWorkBlockMessage = (
  id: string,
  customContent: ReactElement,
): IChatMessage => ({
  // Keyed on the run's first row, so a growing run keeps its identity (and its
  // open/closed state) instead of remounting as each row lands.
  id,
  content: '',
  direction: EMessageDirection.INCOMING,
  // NOT `THINKING`: that is lattice's grouping key, and a block that opted into
  // it would be folded a second time by `groupConsecutiveThinkingMessages`.
  type: EMessageType.DEFAULT,
  widget: { type: EWidgetType.CUSTOM, customContent },
});

/** Builds the header that stands in for one run. */
const buildWorkBlockMessage = (
  rows: IChatMessage[],
  isRunning: boolean,
  isExpanded: boolean,
  onExpandedChange: (expanded: boolean) => void,
  id: string = runId(rows),
): IChatMessage =>
  asWorkBlockMessage(
    id,
    <RainWorkBlock
      rows={rows}
      isRunning={isRunning}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    />,
  );

/**
 * A reasoning row as it is emitted into an OPEN run.
 *
 * It cannot go back as-is: `THINKING` is lattice's grouping key, so a reasoning
 * row at top level is regrouped into a `ChatThinkingMessage` that mounts
 * collapsed, and opening a run would reveal a second closed header instead of
 * the prose. It carries its text in a widget instead, which keeps it out of
 * that grouping and out of the plain-bubble treatment that would make reasoning
 * read as something the agent said aloud.
 *
 * Activity rows need none of this and go back untouched.
 */
const asOpenRow = (row: IChatMessage): IChatMessage =>
  isReasoningRow(row)
    ? {
        ...row,
        type: EMessageType.DEFAULT,
        content: '',
        widget: {
          type: EWidgetType.CUSTOM,
          customContent: <RainReasoningRow content={row.content ?? ''} />,
        },
      }
    : row;

export interface IFoldWorkRunsOptions {
  /** Whether the agent is mid-turn. Only the trailing run can still be live. */
  isGenerating: boolean;
  /**
   * Explicit open/closed choices, by run id. A run that is absent follows the
   * default, which is "open while live" — so a run the operator never touched
   * closes on its own when the work finishes, while one they opened stays open.
   */
  expanded: ReadonlyMap<string, boolean>;
  /** Asks for a run to be opened or closed. */
  onExpandedChange: (runId: string, expanded: boolean) => void;
  /** The agent's own block for a run (`IRainAgentConfig.renderWorkBlock`). */
  renderWorkBlock?: TRainWorkBlockRenderer;
}

/**
 * Folds each maximal run of work rows into one `RainWorkBlock`.
 *
 * The boundary mirrors rain's own TUI (`Transcript::block_spans`): a run is a
 * maximal stretch of consecutive work rows, and — the part that matters — it is
 * computed over the list as RENDERED, so an entry that draws nothing is
 * transparent rather than a seam. Anything the operator must read or act on
 * (the agent speaking, a tool-call card, an approval, an interrupt, a todo or
 * transfer marker) is a real seam and stays exactly where it happened, so
 * nothing actionable is ever hidden behind a collapsed header.
 *
 * An OPEN run is not folded at all: its header is emitted and then its rows
 * follow, unchanged, as the ordinary messages they already were. So "expanded"
 * costs no nested panel and no second scrollbar — it is the transcript it
 * always was, with a header above it.
 *
 * @param messages - The thread's messages, in order
 * @param options - See `IFoldWorkRunsOptions`
 */
export const foldWorkRuns = (
  messages: IChatMessage[],
  {
    isGenerating,
    expanded,
    onExpandedChange,
    renderWorkBlock,
  }: IFoldWorkRunsOptions,
): IChatMessage[] => {
  const out: IChatMessage[] = [];
  let run: IChatMessage[] = [];
  /*
   * Transparent entries met while a run is open, held until it closes so they
   * stay behind the rows they followed. They draw nothing today, so this costs
   * nothing — it keeps the returned order honest for whatever is added to
   * `isTransparent` next.
   */
  let heldTransparent: IChatMessage[] = [];

  // A lone activity row emits no header, and neither does a thread with no work
  // rows at all — hence the header-only block appended after the final flush.
  let hasLiveHeader = false;

  const flush = (isTrailing: boolean): void => {
    if (run.length === 0) {
      out.push(...heldTransparent);
      heldTransparent = [];
      return;
    }
    const id = runId(run);
    const isRunning = isTrailing && isGenerating;
    const isExpanded = expanded.get(id) ?? isRunning;
    const handleExpandedChange = (next: boolean) => onExpandedChange(id, next);
    const custom = renderWorkBlock?.({
      rows: run,
      threadRows: messages,
      isRunning,
      isExpanded,
      onExpandedChange: handleExpandedChange,
    });
    if (custom) {
      if (isRunning) hasLiveHeader = true;
      out.push(asWorkBlockMessage(id, custom.content));
      if (isExpanded) {
        out.push(
          ...run.filter(row => !custom.drawnRowIds.has(row.id)).map(asOpenRow),
        );
      }
    } else if (run.length === 1 && isActivityRow(run[0])) {
      /*
       * A run of one activity row is not a run: collapsing costs a click to read
       * a single line and hides nothing worth hiding. Rain's TUI draws the same
       * line (`items.len() == 1` renders as a plain row, never a group).
       *
       * A lone reasoning stretch still gets a block — that one is multi-paragraph
       * chain-of-thought, which is worth hiding even alone.
       */
      out.push(run[0]);
    } else {
      if (isRunning) hasLiveHeader = true;
      out.push(
        buildWorkBlockMessage(run, isRunning, isExpanded, handleExpandedChange),
      );
      // Open means unfolded: the rows go back into the transcript.
      if (isExpanded) out.push(...run.map(asOpenRow));
    }
    run = [];
    out.push(...heldTransparent);
    heldTransparent = [];
  };

  for (const message of messages) {
    if (isWorkRow(message)) {
      run.push(message);
      continue;
    }
    // Neither joins the run nor ends it.
    if (isTransparent(message)) {
      if (run.length > 0) heldTransparent.push(message);
      else out.push(message);
      continue;
    }
    flush(false);
    out.push(message);
  }
  flush(true);

  // Working with nothing on screen to say so: between outputs, or running tools
  // that render no card. The same header every other run gets, minus the rows.
  if (isGenerating && !hasLiveHeader) {
    const custom = renderWorkBlock?.({
      rows: [],
      threadRows: messages,
      isRunning: true,
      isExpanded: false,
      onExpandedChange: ignoreExpandedChange,
    });
    out.push(
      custom
        ? asWorkBlockMessage(LIVE_RUN_ID, custom.content)
        : buildWorkBlockMessage(
            [],
            true,
            false,
            ignoreExpandedChange,
            LIVE_RUN_ID,
          ),
    );
  }

  return out;
};
